import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import YouTube from "react-youtube";
import {
  Users,
  Menu,
  Music2,
  SkipForward,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Loader2,
  WifiOff,
  UserX,
  Ban,
  X,
  GripVertical,
  Plus,
  Search,
} from "lucide-react";
import {
  getRoom,
  getQueue,
  removeFromQueue,
  reorderQueue,
  kickGuest,
  banGuest,
  searchKaraoke,
  searchYouTube,
  addToQueueAsHost,
} from "@/lib/api";
import { getSocket } from "@/lib/socket";
import { formatDuration, formatViews, decodeHtmlEntities } from "@/lib/format";
import { APP_TEXT_LOGO_URL } from "@/constants/branding";
import { clearSession } from "@/lib/session";

const INTERLUDE_DURATION_MS = 10000;
const SONG_END_EARLY_BUFFER_SECONDS = 10;
const SCORE_SCREEN_AUDIO_URL = "/media/score_card.mp3";
const INTERLUDE_PHRASE_BANDS = [
  {
    min: 96,
    phrases: [
      "You're a great singer!",
      "That was seriously impressive.",
      "You lit up the room with that one.",
      "That sounded like a headline performance.",
    ],
  },
  {
    min: 90,
    phrases: [
      "You sounded really good out there.",
      "That was a strong performance.",
      "You had the whole room with you.",
      "That chorus landed beautifully.",
    ],
  },
  {
    min: 84,
    phrases: [
      "Nice job, that was a fun one.",
      "You kept the energy up all the way through.",
      "That was smooth and confident.",
      "The room was into that performance.",
    ],
  },
  {
    min: 78,
    phrases: [
      "Good vibes all around.",
      "That was a fun performance.",
      "You kept the karaoke spirit going.",
      "Strong effort, keep them coming.",
    ],
  },
];
const INTERLUDE_LABELS = [
  { min: 98, label: "Legend Mode" },
  { min: 94, label: "Main Event" },
  { min: 90, label: "Crowd Hero" },
  { min: 86, label: "Golden Voice" },
  { min: 82, label: "House Favorite" },
  { min: 78, label: "Smooth Operator" },
];

function getInterludePhrase(score) {
  const phrases =
    INTERLUDE_PHRASE_BANDS.find((entry) => score >= entry.min)?.phrases ||
    INTERLUDE_PHRASE_BANDS[INTERLUDE_PHRASE_BANDS.length - 1].phrases;

  return phrases[Math.floor(Math.random() * phrases.length)];
}

function createInterludeResult() {
  const score = Math.floor(78 + Math.random() * 22);
  const label =
    INTERLUDE_LABELS.find((entry) => score >= entry.min)?.label || "Late Night Star";
  const phrase = getInterludePhrase(score);
  return { score, label, phrase };
}

export default function HostRoom() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const code = (roomCode || "").toUpperCase();

  const [room, setRoom] = useState(null);
  const [queue, setQueue] = useState([]);
  const [guests, setGuests] = useState([]);
  const [hostToken, setHostToken] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);
  const [showGuests, setShowGuests] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [showQueue, setShowQueue] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 1024;
  });
  const [showAddSong, setShowAddSong] = useState(false);
  const [hostNickname, setHostNickname] = useState("Host");
  const [uiVisible, setUiVisible] = useState(true);
  const [roomCodeCopied, setRoomCodeCopied] = useState(false);
  const [qrPulseVisible, setQrPulseVisible] = useState(true);
  const [interlude, setInterlude] = useState(null);
  const [interludeNow, setInterludeNow] = useState(Date.now());
  const [guestCountBump, setGuestCountBump] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem("host_audio_unlocked") === "1";
    } catch (_e) {
      return false;
    }
  });
  const [isFullscreen, setIsFullscreen] = useState(() =>
    typeof document !== "undefined" ? Boolean(document.fullscreenElement) : false
  );
  const hideTimerRef = useRef(null);
  const qrPulseIntervalRef = useRef(null);
  const qrPulseHideTimeoutRef = useRef(null);
  const roomCodeCopyTimeoutRef = useRef(null);
  const guestCountBumpTimeoutRef = useRef(null);
  const prevGuestCountRef = useRef(guests.length);
  const overflowMenuRef = useRef(null);
  const overflowButtonRef = useRef(null);

  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const progressInterval = useRef(null);
  const currentVideoRef = useRef(null);
  const interludeTimeoutRef = useRef(null);
  const interludeTickRef = useRef(null);
  const interludeAudioRef = useRef(null);
  const audioUnlockInFlightRef = useRef(false);
  const songEndTriggeredRef = useRef(null);

  const joinUrl = `${window.location.origin}/join/${code}`;
  const current = queue[0];
  const interludeActive = Boolean(interlude);
  const interludeRemainingMs = interlude
    ? Math.max(0, interlude.endsAt - interludeNow)
    : 0;
  const floatingQrEligible = Boolean(current) && (!showQueue || !uiVisible);
  const QR_VISIBLE_MS = 15000;
  const QR_HIDDEN_MS = 15000;
  const QR_CYCLE_MS = QR_VISIBLE_MS + QR_HIDDEN_MS;

  const stopInterludeSound = useCallback(() => {
    const audio = interludeAudioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const ensureInterludeAudio = useCallback(() => {
    if (typeof window === "undefined") return null;

    if (!interludeAudioRef.current) {
      const audio = new Audio(SCORE_SCREEN_AUDIO_URL);
      audio.preload = "auto";
      audio.volume = 0.65;
      interludeAudioRef.current = audio;
    }

    return interludeAudioRef.current;
  }, []);

  const tryUnlockInterludeAudio = useCallback(async () => {
    if (audioUnlocked || audioUnlockInFlightRef.current) return audioUnlocked;

    const audio = ensureInterludeAudio();
    if (!audio) return false;

    audioUnlockInFlightRef.current = true;
    try {
      audio.currentTime = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      setAudioUnlocked(true);
      localStorage.setItem("host_audio_unlocked", "1");
      return true;
    } catch (_e) {
      return false;
    } finally {
      audioUnlockInFlightRef.current = false;
    }
  }, [audioUnlocked, ensureInterludeAudio]);

  const playInterludeSound = useCallback(() => {
    const audio = ensureInterludeAudio();
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Browser autoplay policies may block playback until a user gesture.
    });
  }, [ensureInterludeAudio]);

  useEffect(() => {
    if (audioUnlocked) return;

    let active = true;
    const onFirstInteraction = (event) => {
      if (event.type === "keydown" && event.repeat) return;
      if (!active) return;

      void tryUnlockInterludeAudio().then((unlocked) => {
        if (!unlocked || !active) return;
        window.removeEventListener("keydown", onFirstInteraction);
        window.removeEventListener("pointerdown", onFirstInteraction);
      });
    };

    window.addEventListener("keydown", onFirstInteraction);
    window.addEventListener("pointerdown", onFirstInteraction);

    return () => {
      active = false;
      window.removeEventListener("keydown", onFirstInteraction);
      window.removeEventListener("pointerdown", onFirstInteraction);
    };
  }, [audioUnlocked, tryUnlockInterludeAudio]);

  // Load saved host token
  useEffect(() => {
    const saved = localStorage.getItem(`host_${code}`);
    if (!saved) {
      toast.error("Host session not found. Please re-create the room.");
      navigate("/host");
      return;
    }
    const { host_token, host_nickname } = JSON.parse(saved);
    setHostToken(host_token);
    if (host_nickname) setHostNickname(host_nickname);
  }, [code, navigate]);

  // Load room + initial queue
  useEffect(() => {
    getRoom(code)
      .then(setRoom)
      .catch(() => {
        toast.error("Room not found");
        navigate("/");
      });
    getQueue(code).then((d) => setQueue(d.queue || []));
  }, [code, navigate]);

  // Socket.IO
  useEffect(() => {
    const sock = getSocket();

    const onConnect = () => {
      setSocketConnected(true);
      sock.emit("host:join", { room_code: code });
    };
    const onDisconnect = () => setSocketConnected(false);
    const onRoomUpdate = (data) => {
      if (data.room) setRoom((r) => ({ ...(r || {}), ...data.room }));
      if (data.guests) setGuests(data.guests);
      if (data.queue) setQueue(data.queue);
    };
    const onQueueUpdate = (data) => setQueue(data.queue || []);
    const onGuestUpdate = (data) => setGuests(data.guests || []);
    const onNotify = (n) => {
      if (n.type === "guest_joined") toast(`${n.name} joined the room`);
      if (n.type === "song_added")
        toast(`${n.name} added "${decodeHtmlEntities(n.title || "").slice(0, 40)}"`);
    };
    const onRoomClosed = (data) => {
      const reason = data?.reason || "closed";
      const msg =
        reason === "host_disconnected"
          ? "Session closed — host was disconnected"
          : reason === "host_idle"
          ? "Session closed — host was inactive"
          : "Room closed";
      toast.error(msg);
      localStorage.removeItem(`host_${code}`);
      clearSession();
      setTimeout(() => navigate("/"), 1200);
    };

    sock.on("connect", onConnect);
    sock.on("disconnect", onDisconnect);
    sock.on("room:update", onRoomUpdate);
    sock.on("queue:update", onQueueUpdate);
    sock.on("guest:update", onGuestUpdate);
    sock.on("notify", onNotify);
    sock.on("room:closed", onRoomClosed);

    if (sock.connected) onConnect();

    return () => {
      sock.off("connect", onConnect);
      sock.off("disconnect", onDisconnect);
      sock.off("room:update", onRoomUpdate);
      sock.off("queue:update", onQueueUpdate);
      sock.off("guest:update", onGuestUpdate);
      sock.off("notify", onNotify);
      sock.off("room:closed", onRoomClosed);
    };
  }, [code, navigate]);

  // Auto-load next video when queue head changes
  useEffect(() => {
    if (!playerReady || !playerRef.current) return;
    const newId = current?.video_id || null;
    if (newId && currentVideoRef.current !== newId) {
      currentVideoRef.current = newId;
      try {
        if (interludeActive) {
          playerRef.current.cueVideoById?.(newId);
        } else {
          playerRef.current.loadVideoById(newId);
        }
      } catch (e) {
        /* ignore */
      }
    } else if (!newId && currentVideoRef.current) {
      currentVideoRef.current = null;
      try {
        playerRef.current.stopVideo();
      } catch (e) {
        /* ignore */
      }
    }
  }, [current, interludeActive, playerReady]);

  useEffect(() => {
    if (!interludeActive) {
      if (interludeTimeoutRef.current) {
        clearTimeout(interludeTimeoutRef.current);
        interludeTimeoutRef.current = null;
      }
      if (interludeTickRef.current) {
        clearInterval(interludeTickRef.current);
        interludeTickRef.current = null;
      }
      stopInterludeSound();
      return;
    }

    setInterludeNow(Date.now());
    playInterludeSound();
    interludeTickRef.current = setInterval(() => {
      setInterludeNow(Date.now());
    }, 250);
    interludeTimeoutRef.current = setTimeout(() => {
      setInterlude(null);
      try {
        if (!isPlaying) {
          playerRef.current?.playVideo?.();
        }
      } catch (_e) {
        /* ignore */
      }
    }, Math.max(0, interlude.endsAt - Date.now()));

    return () => {
      if (interludeTimeoutRef.current) {
        clearTimeout(interludeTimeoutRef.current);
        interludeTimeoutRef.current = null;
      }
      if (interludeTickRef.current) {
        clearInterval(interludeTickRef.current);
        interludeTickRef.current = null;
      }
      stopInterludeSound();
    };
  }, [interlude, interludeActive, isPlaying, playInterludeSound, stopInterludeSound]);

  useEffect(() => {
    return () => {
      const audio = interludeAudioRef.current;
      if (!audio) return;
      audio.pause();
      audio.src = "";
      interludeAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    songEndTriggeredRef.current = null;
  }, [current?.id]);

  const triggerSongEnded = useCallback(() => {
    if (!current?.id || songEndTriggeredRef.current === current.id) return;

    songEndTriggeredRef.current = current.id;
    setIsPlaying(false);

    const upcomingSong = queue[1];
    setInterlude({
      ...createInterludeResult(),
      endsAt: Date.now() + INTERLUDE_DURATION_MS,
      upcomingTitle: upcomingSong?.title || null,
      finishedSong: current
        ? {
            title: current.title,
            requested_by_name: current.requested_by_name,
          }
        : null,
    });

    const sock = getSocket();
    sock.emit("host:songEnded", {
      room_code: code,
      queue_id: current.id,
    });
  }, [code, current, queue]);

  // Progress poll
  useEffect(() => {
    if (!playerReady) return;
    progressInterval.current = setInterval(() => {
      try {
        const p = playerRef.current?.getCurrentTime?.() || 0;
        const d = playerRef.current?.getDuration?.() || 0;
        setProgress(p);
        setDuration(d);

        if (
          isPlaying &&
          !interludeActive &&
          current?.id &&
          d > SONG_END_EARLY_BUFFER_SECONDS &&
          d - p <= SONG_END_EARLY_BUFFER_SECONDS
        ) {
          triggerSongEnded();
        }
      } catch (e) {
        // ignore
      }
    }, 500);
    return () => clearInterval(progressInterval.current);
  }, [current?.id, interludeActive, isPlaying, playerReady, triggerSongEnded]);

  const broadcastPlayerState = (state) => {
    const sock = getSocket();
    sock.emit("host:playerState", {
      room_code: code,
      state,
      current_time: playerRef.current?.getCurrentTime?.() || 0,
      duration: playerRef.current?.getDuration?.() || 0,
      video_id: current?.video_id,
      title: current?.title,
      requested_by_name: current?.requested_by_name,
    });
  };

  const handlePlayerReady = (e) => {
    playerRef.current = e.target;
    setPlayerReady(true);
    try {
      e.target.setVolume(volume);
    } catch (_e) {
      /* ignore */
    }
  };

  const handleStateChange = (e) => {
    // 0: ended, 1: playing, 2: paused, 3: buffering, 5: cued
    const s = e.data;
    if (s === 1) {
      if (interludeActive) {
        try {
          playerRef.current?.pauseVideo?.();
        } catch (_e) {
          /* ignore */
        }
        setIsPlaying(false);
        broadcastPlayerState("paused");
        return;
      }
      setIsPlaying(true);
      broadcastPlayerState("playing");
    } else if (s === 2) {
      setIsPlaying(false);
      broadcastPlayerState("paused");
    } else if (s === 0) {
      triggerSongEnded();
    } else if (s === 3) {
      broadcastPlayerState("buffering");
    }
  };

  const togglePlay = () => {
    if (!playerRef.current || interludeActive) return;
    try {
      if (isPlaying) playerRef.current.pauseVideo();
      else playerRef.current.playVideo();
    } catch (_e) {
      /* ignore */
    }
  };

  const skip = () => {
    if (!current) return;
    if (interludeActive) {
      setInterlude(null);
    }
    const sock = getSocket();
    sock.emit("host:skip", { room_code: code, queue_id: current.id });
  };

  const removeItem = async (id) => {
    try {
      await removeFromQueue(code, id, { host_token: hostToken });
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  const handleKick = async (guestId, nickname) => {
    try {
      await kickGuest(code, guestId, hostToken);
      toast.success(`${nickname} removed`);
    } catch {
      toast.error("Failed to kick");
    }
  };

  const handleBan = async (guestId, nickname) => {
    if (!window.confirm(`Ban ${nickname}? They won't be able to rejoin.`)) return;
    try {
      await banGuest(code, guestId, hostToken);
      toast.success(`${nickname} banned`);
    } catch {
      toast.error("Failed to ban");
    }
  };

  // Drag & drop reorder for the upcoming queue
  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDropOn = async (targetId) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    // Build new order, never moving the now-playing item (queue[0])
    const head = queue[0];
    const rest = queue.slice(1);
    const fromIdx = rest.findIndex((q) => q.id === dragId);
    const toIdx = rest.findIndex((q) => q.id === targetId);
    if (fromIdx < 0 || toIdx < 0) {
      setDragId(null);
      return;
    }
    const newRest = [...rest];
    const [moved] = newRest.splice(fromIdx, 1);
    newRest.splice(toIdx, 0, moved);
    const newOrder = head ? [head, ...newRest] : newRest;
    // optimistic
    setQueue(newOrder);
    setDragId(null);
    try {
      await reorderQueue(
        code,
        newOrder.map((q) => q.id),
        hostToken
      );
    } catch {
      toast.error("Failed to reorder");
    }
  };

  const toggleMute = () => {
    if (!playerRef.current || interludeActive) return;
    try {
      if (muted) {
        playerRef.current.unMute();
        setMuted(false);
      } else {
        playerRef.current.mute();
        setMuted(true);
      }
    } catch (_e) {
      /* ignore */
    }
  };

  const onVolumeChange = (v) => {
    if (interludeActive) return;
    setVolume(v);
    try {
      playerRef.current?.setVolume(v);
      if (v > 0 && muted) {
        playerRef.current?.unMute();
        setMuted(false);
      }
    } catch (_e) {
      /* ignore */
    }
  };

  const goFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  };

  const copyRoomCode = useCallback(async () => {
    if (!code) return;

    const fallbackCopy = () => {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (!copied) throw new Error("copy_failed");
    };

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        fallbackCopy();
      }

      setRoomCodeCopied(true);
      if (roomCodeCopyTimeoutRef.current) {
        clearTimeout(roomCodeCopyTimeoutRef.current);
      }
      roomCodeCopyTimeoutRef.current = setTimeout(() => {
        setRoomCodeCopied(false);
      }, 1100);
    } catch (_err) {
      toast.error("Could not copy room code.");
    }
  }, [code]);

  const openAddSong = useCallback(() => {
    void tryUnlockInterludeAudio();
    setShowAddSong(true);
  }, [tryUnlockInterludeAudio]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
      } else if (e.code === "ArrowRight") {
        skip();
      } else if (e.code === "KeyF") {
        goFullscreen();
      } else if (e.code === "KeyG") {
        setShowGuests((s) => !s);
      } else if (e.code === "KeyQ") {
        setShowQueue((s) => !s);
      } else if (e.code === "KeyA") {
        setShowAddSong((s) => !s);
      } else if (e.code === "Escape") {
        setShowAddSong(false);
        setShowGuests(false);
        setShowOverflowMenu(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, current]);

  // Cinema mode: auto-hide topbar + controls after 3s of inactivity.
  // Hide timer is started only from user input (mousemove/keydown/touchstart).
  const anyModalOpen = showAddSong || showGuests;
  const cinemaContextActive = Boolean(current) || interludeActive;
  useEffect(() => {
    const scheduleHide = () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
      if (!cinemaContextActive || anyModalOpen) return;
      hideTimerRef.current = setTimeout(() => setUiVisible(false), 3000);
    };
    const wake = () => {
      setUiVisible(true);
      scheduleHide();
    };
    // Modals always keep controls visible for room management actions.
    if (anyModalOpen) {
      setUiVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
      return;
    }
    // Only force visible in true idle state (no song and no interlude).
    if (!cinemaContextActive) {
      setUiVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
      return;
    }
    window.addEventListener("mousemove", wake);
    window.addEventListener("keydown", wake);
    window.addEventListener("touchstart", wake);
    return () => {
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("keydown", wake);
      window.removeEventListener("touchstart", wake);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    };
  }, [anyModalOpen, cinemaContextActive]);

  // Floating join QR cadence: 15s visible every 30s cycle (15s hidden).
  // This runs independently of autohide (uiVisible) to avoid state conflicts.
  useEffect(() => {
    const clearPulseTimers = () => {
      if (qrPulseIntervalRef.current) {
        clearInterval(qrPulseIntervalRef.current);
        qrPulseIntervalRef.current = null;
      }
      if (qrPulseHideTimeoutRef.current) {
        clearTimeout(qrPulseHideTimeoutRef.current);
        qrPulseHideTimeoutRef.current = null;
      }
    };

    if (!floatingQrEligible) {
      clearPulseTimers();
      setQrPulseVisible(true);
      return;
    }

    setQrPulseVisible(true);
    qrPulseHideTimeoutRef.current = setTimeout(() => {
      setQrPulseVisible(false);
    }, QR_VISIBLE_MS);

    qrPulseIntervalRef.current = setInterval(() => {
      setQrPulseVisible(true);
      if (qrPulseHideTimeoutRef.current) {
        clearTimeout(qrPulseHideTimeoutRef.current);
      }
      qrPulseHideTimeoutRef.current = setTimeout(() => {
        setQrPulseVisible(false);
      }, QR_VISIBLE_MS);
    }, QR_CYCLE_MS);

    return clearPulseTimers;
  }, [floatingQrEligible, QR_CYCLE_MS, QR_VISIBLE_MS]);

  useEffect(() => {
    if (prevGuestCountRef.current === guests.length) return;

    setGuestCountBump(true);
    if (guestCountBumpTimeoutRef.current) {
      clearTimeout(guestCountBumpTimeoutRef.current);
    }
    guestCountBumpTimeoutRef.current = setTimeout(() => {
      setGuestCountBump(false);
    }, 280);
    prevGuestCountRef.current = guests.length;
  }, [guests.length]);

  useEffect(() => {
    return () => {
      if (roomCodeCopyTimeoutRef.current) {
        clearTimeout(roomCodeCopyTimeoutRef.current);
      }
      if (guestCountBumpTimeoutRef.current) {
        clearTimeout(guestCountBumpTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    if (!showOverflowMenu) return;

    const onPointerDown = (event) => {
      const target = event.target;
      if (
        overflowMenuRef.current?.contains(target) ||
        overflowButtonRef.current?.contains(target)
      ) {
        return;
      }
      setShowOverflowMenu(false);
    };

    const onEscape = (event) => {
      if (event.key === "Escape") setShowOverflowMenu(false);
    };

    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onEscape);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onEscape);
    };
  }, [showOverflowMenu]);

  const ytOpts = {
    width: "100%",
    height: "100%",
    playerVars: {
      autoplay: 1,
      controls: 0,
      modestbranding: 1,
      rel: 0,
      iv_load_policy: 3,
      disablekb: 1,
      fs: 0,
      playsinline: 1,
    },
  };

  return (
    <div
      ref={containerRef}
      className={`ambient-bg min-h-screen relative overflow-hidden ${
        !uiVisible ? "cursor-none" : ""
      }`}
      data-testid="host-room-page"
    >
      <div className="grain" />
      {/* Top bar */}
      <header
        className={`fixed top-0 left-0 right-0 z-30 rounded-none glass shadow-[0_10px_26px_-16px_rgba(0,0,0,0.9)] px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:gap-3 transition-all duration-500 ${
          uiVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-full pointer-events-none"
        }`}
        onMouseEnter={() => setUiVisible(true)}
        data-testid="host-topbar"
      >
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 justify-self-start">
          <div className="w-32 h-11 sm:w-44 sm:h-14 lg:w-52 lg:h-16 flex-shrink-0">
            <img
              src={APP_TEXT_LOGO_URL}
              alt="OpenKTV"
              className="w-full h-full object-contain object-left"
            />
          </div>
          <div className="min-w-0">
            <div
              className="text-white font-bold text-lg sm:text-2xl lg:text-[1.7rem] font-[Outfit] leading-tight truncate"
              data-testid="room-name-display"
            >
              {room?.room_name || "Loading..."}
            </div>
            <div className="text-zinc-400 text-sm sm:text-base truncate leading-tight">
              Hosted by {hostNickname || "Host"}
            </div>
          </div>
        </div>

        <button
          data-testid="topbar-add-song"
          onClick={openAddSong}
          title="Add a song as host (A)"
          className="justify-self-center inline-flex items-center gap-2 sm:gap-2.5 px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl border border-purple-300/25 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white text-sm sm:text-base font-semibold font-[Outfit] shadow-[0_12px_24px_-14px_rgba(168,85,247,0.9)] hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.985] transition-all duration-200"
        >
          <Plus size={18} strokeWidth={2.5} />
          <span>Add Song</span>
        </button>

        <div className="flex items-center gap-2 sm:gap-3 justify-self-end">
          <div className="hidden sm:flex items-center gap-2 rounded-2xl bg-zinc-950/40 px-3 py-2">
            <Users size={16} className="text-purple-300" />
            <div className="flex items-baseline gap-1.5 text-zinc-300">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={guests.length}
                  initial={{ opacity: 0.55, y: 4, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: guestCountBump ? 1.1 : 1 }}
                  exit={{ opacity: 0.3, y: -4, scale: 0.9 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="text-sm sm:text-base font-medium"
                  data-testid="guest-count"
                >
                  {guests.length}
                </motion.span>
              </AnimatePresence>
              <span className="text-xs sm:text-sm text-zinc-400">Guests</span>
            </div>
          </div>

          <button
            onClick={copyRoomCode}
            title="Copy room code"
            className="group min-w-[108px] sm:min-w-[132px] rounded-2xl bg-zinc-950/40 px-3 py-2 text-left transition-colors hover:bg-zinc-900/70"
          >
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-zinc-500 leading-none">
              Room
            </div>
            <div
              className="mt-0.5 text-sm sm:text-base lg:text-lg font-semibold tracking-[0.18em] text-white font-[Outfit] leading-tight"
              data-testid="room-code-display"
            >
              {code}
            </div>
            <AnimatePresence>
              {roomCodeCopied && (
                <motion.div
                  initial={{ opacity: 0, y: 4, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -2, scale: 0.95 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="text-[10px] sm:text-xs text-emerald-300 mt-0.5"
                >
                  Copied!
                </motion.div>
              )}
            </AnimatePresence>
          </button>

          <div className="relative">
            <button
              ref={overflowButtonRef}
              onClick={() => setShowOverflowMenu((s) => !s)}
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-zinc-900/70 border border-white/10 text-zinc-200 hover:text-white hover:border-purple-400/45 hover:bg-zinc-900 flex items-center justify-center transition-all"
              aria-label="Open room controls"
              aria-expanded={showOverflowMenu}
              aria-haspopup="menu"
            >
              <Menu size={20} />
            </button>
            {showOverflowMenu && (
              <div
                ref={overflowMenuRef}
                className="absolute right-0 mt-2 w-56 rounded-2xl border border-white/10 bg-zinc-950/95 backdrop-blur-xl text-zinc-100 shadow-[0_16px_30px_-18px_rgba(0,0,0,0.9)] overflow-hidden"
                role="menu"
              >
                <button
                  data-testid="topbar-toggle-queue"
                  onClick={() => {
                    setShowQueue((s) => !s);
                    setShowOverflowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-zinc-900 transition-colors"
                  role="menuitem"
                >
                  <Music2 size={16} className="text-purple-300" />
                  <span>{showQueue ? "Hide Queue" : "Show Queue"}</span>
                  <span className="ml-auto text-xs tracking-widest opacity-60">Q</span>
                </button>
                <button
                  data-testid="open-guests-panel"
                  onClick={() => {
                    setShowGuests(true);
                    setShowOverflowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-zinc-900 transition-colors"
                  role="menuitem"
                >
                  <Users size={16} className="text-purple-300" />
                  <span>Manage Guests</span>
                  <span className="ml-auto text-xs tracking-widest opacity-60">G</span>
                </button>
                <button
                  onClick={() => {
                    goFullscreen();
                    setShowOverflowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-zinc-900 transition-colors"
                  role="menuitem"
                >
                  <Maximize size={16} className="text-purple-300" />
                  <span>{isFullscreen ? "Exit Full Screen" : "Full Screen"}</span>
                  <span className="ml-auto text-xs tracking-widest opacity-60">F</span>
                </button>
                {!socketConnected && (
                  <div className="mx-2 mt-1 mb-2 px-2.5 py-2 text-xs text-red-300 bg-red-500/10 border border-red-500/25 rounded-xl flex items-center gap-2">
                    <WifiOff size={13} /> Reconnecting...
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="sm:hidden flex items-center gap-1.5 rounded-full bg-zinc-950/40 px-2.5 py-1.5">
            <Users size={14} className="text-purple-300" />
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={guests.length}
                initial={{ opacity: 0.5, y: 3, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: guestCountBump ? 1.08 : 1 }}
                exit={{ opacity: 0.3, y: -2, scale: 0.9 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="text-xs text-zinc-300"
              >
                {guests.length}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {!uiVisible && current && !interludeActive && (
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="fixed top-4 sm:top-5 left-4 sm:left-5 md:left-6 z-[35] pointer-events-none"
            data-testid="host-autohide-branding-overlay"
          >
            <div className="relative overflow-hidden rounded-xl pl-3 pr-8 sm:pr-10 py-2 bg-[linear-gradient(90deg,rgba(0,0,0,0.82)_0%,rgba(255,255,255,0.28)_62%,rgba(255,255,255,0)_100%)]">
              <div className="w-36 h-10 sm:w-44 sm:h-12 md:w-52 md:h-14 opacity-95">
                <img
                  src={APP_TEXT_LOGO_URL}
                  alt="OpenKTV"
                  className="w-full h-full object-contain drop-shadow-[0_8px_26px_rgba(0,0,0,0.65)]"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-2 px-2 pb-2 h-[100dvh] transition-[padding] duration-500 ${
          uiVisible ? "pt-[74px] sm:pt-[86px]" : "pt-0"
        }`}
      >
        {/* Left/main: player (controls float over the bottom) */}
        <div
          className={`${
            showQueue && uiVisible ? "col-span-1 lg:col-span-8" : "col-span-1 lg:col-span-12"
          } relative min-h-0 transition-all duration-300`}
        >
          {/* Player area — seamless, no rounded corners on the iframe frame */}
          <div className="absolute inset-0 overflow-hidden bg-black">
            {current ? (
              <>
                <div
                  className={`absolute inset-0 transition-opacity duration-300 ${
                    interludeActive ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <YouTube
                    videoId={current.video_id}
                    opts={ytOpts}
                    onReady={handlePlayerReady}
                    onStateChange={handleStateChange}
                    onError={() => {
                      toast.error("Video unavailable — skipping");
                      skip();
                    }}
                    className="w-full h-full"
                    iframeClassName="w-full h-full"
                  />
                </div>
                {/* Bottom gradient for control readability, fades with UI */}
                <div
                  className={`absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/80 to-transparent pointer-events-none transition-opacity duration-500 ${
                    uiVisible && !interludeActive ? "opacity-100" : "opacity-0"
                  }`}
                />
                {/* Floating join QR when queue sidebar is hidden */}
                <AnimatePresence>
                  {(!showQueue || !uiVisible) && qrPulseVisible && !interludeActive && (
                    <FloatingQR joinUrl={joinUrl} code={code} />
                  )}
                </AnimatePresence>
              </>
            ) : !interludeActive ? (
              <IdleScreen joinUrl={joinUrl} code={code} guests={guests.length} />
            ) : null}

            <AnimatePresence>
              {interludeActive && (
                <KaraokeInterludeOverlay
                  countdownMs={interludeRemainingMs}
                  currentTitle={interlude?.upcomingTitle || current?.title}
                  hasUpcomingSong={Boolean(interlude?.upcomingTitle)}
                  finishedSong={interlude?.finishedSong}
                  label={interlude?.label}
                  phrase={interlude?.phrase}
                  score={interlude?.score}
                />
              )}
            </AnimatePresence>
          </div>

          {/* Controls — floating over the bottom of the video */}
          <div
            className={`absolute bottom-2 left-2 right-2 z-20 glass rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 flex items-center flex-wrap lg:flex-nowrap gap-2 sm:gap-4 transition-all duration-500 ${
              uiVisible && !interludeActive
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-[120%] pointer-events-none"
            }`}
            data-testid="host-controls"
            onMouseEnter={() => setUiVisible(true)}
          >
            <button
              data-testid="player-play-pause-btn"
              onClick={togglePlay}
              disabled={!current || interludeActive}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center transition-all shadow-[0_0_30px_rgba(139,92,246,0.5)]"
            >
              {isPlaying ? (
                <Pause size={22} className="text-white" />
              ) : (
                <Play size={22} className="text-white ml-1" />
              )}
            </button>
            <button
              data-testid="player-skip-btn"
              onClick={skip}
              disabled={!current}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 flex items-center justify-center transition-all"
            >
              <SkipForward size={18} className="text-white" />
            </button>

            <div className="order-3 basis-full lg:order-none lg:basis-auto flex-1 min-w-0 px-1 sm:px-2">
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-1.5">
                <span>{formatDuration(progress)}</span>
                <span>{formatDuration(duration)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
                  style={{
                    width: duration
                      ? `${Math.min(100, (progress / duration) * 100)}%`
                      : "0%",
                  }}
                />
              </div>
            </div>

            <button
              data-testid="player-mute-btn"
              onClick={toggleMute}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-all"
            >
              {muted ? (
                <VolumeX size={18} className="text-white" />
              ) : (
                <Volume2 size={18} className="text-white" />
              )}
            </button>

            <input
              data-testid="player-volume-slider"
              type="range"
              min={0}
              max={100}
              value={volume}
              onChange={(e) => onVolumeChange(parseInt(e.target.value, 10))}
              className="hidden md:block w-24 lg:w-32 accent-purple-500"
            />

            <button
              data-testid="player-fullscreen-btn"
              onClick={goFullscreen}
              className="hidden sm:flex w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-zinc-800 hover:bg-zinc-700 items-center justify-center transition-all"
            >
              <Maximize size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* Right: queue sidebar + persistent QR */}
        {showQueue && uiVisible && (
        <div className="hidden lg:flex col-span-4 flex-col gap-6 min-h-0 pt-2" data-testid="queue-sidebar">
          {/* Persistent QR/Code */}
          <div className="glass rounded-3xl p-5 flex items-center gap-4">
            <div className="bg-white p-2 rounded-2xl">
              <QRCodeSVG
                value={joinUrl}
                size={92}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                data-testid="join-qr-code"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Scan to Join
              </div>
              <div className="text-white font-semibold text-lg truncate font-[Outfit]">
                openktv.app
              </div>
              <div className="text-zinc-400 text-xs truncate">/join/{code}</div>
            </div>
          </div>

          {/* Queue */}
          <div className="glass rounded-3xl flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Music2 size={18} className="text-purple-400" />
                <h3 className="text-white font-bold font-[Outfit] text-lg">
                  Up Next
                </h3>
                <span className="text-zinc-500 text-sm">
                  ({Math.max(queue.length - 1, 0)})
                </span>
              </div>
            </div>
            <div
              className="flex-1 overflow-y-auto p-4 space-y-3"
              data-testid="queue-list"
            >
              <AnimatePresence mode="popLayout">
                {queue.slice(1).length === 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-12 text-zinc-500"
                  >
                    <Music2
                      size={32}
                      className="mx-auto mb-3 opacity-40"
                    />
                    <div className="text-sm">No songs queued yet</div>
                    <div className="text-xs text-zinc-600 mt-1">
                      Have guests scan the QR to start
                    </div>
                  </motion.div>
                )}
                {queue.slice(1).map((q, idx) => (
                  <motion.div
                    layout
                    key={q.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.25 }}
                    draggable
                    onDragStart={() => onDragStart(q.id)}
                    onDragOver={onDragOver}
                    onDrop={() => onDropOn(q.id)}
                    onDragEnd={() => setDragId(null)}
                    className={`flex items-center gap-2.5 p-2.5 rounded-2xl bg-zinc-900/50 border transition-colors group cursor-grab active:cursor-grabbing ${
                      dragId === q.id
                        ? "border-purple-500/70 opacity-50"
                        : "border-white/5 hover:border-purple-500/30"
                    }`}
                    data-testid={`queue-item-${q.id}`}
                  >
                    <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                      <GripVertical
                        size={14}
                        className="text-zinc-600 group-hover:text-purple-400 transition-colors"
                      />
                      <div className="w-6 h-6 rounded-full bg-zinc-800 text-zinc-400 flex items-center justify-center text-[10px] font-bold">
                        {idx + 1}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate">
                        {decodeHtmlEntities(q.title)}
                      </div>
                      <div className="text-zinc-500 text-xs truncate">
                        {q.requested_by_name} · {formatDuration(q.duration)}
                      </div>
                    </div>
                    <button
                      data-testid={`remove-queue-${q.id}`}
                      onClick={() => removeItem(q.id)}
                      className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 text-lg w-6 transition-opacity"
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
        )}

        <AnimatePresence>
          {showQueue && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/55 backdrop-blur-[2px] z-40 lg:hidden"
                onClick={() => setShowQueue(false)}
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 260 }}
                className="fixed right-0 top-0 bottom-0 w-full sm:w-[440px] z-50 glass border-l border-white/10 flex flex-col lg:hidden"
                data-testid="queue-sidebar-mobile"
              >
                <div className="px-4 sm:px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Queue Manager
                    </div>
                    <div className="text-white font-bold text-lg font-[Outfit] truncate">
                      Up Next ({Math.max(queue.length - 1, 0)})
                    </div>
                  </div>
                  <button
                    onClick={() => setShowQueue(false)}
                    className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-white"
                    data-testid="close-queue-sidebar-mobile"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="px-4 sm:px-5 py-4 border-b border-white/5 flex items-center gap-3">
                  <div className="bg-white p-2 rounded-2xl flex-shrink-0">
                    <QRCodeSVG
                      value={joinUrl}
                      size={84}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="M"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                      Scan to Join
                    </div>
                    <div className="text-white font-semibold text-base truncate font-[Outfit]">
                      openktv.app/join/{code}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="queue-list-mobile">
                  <AnimatePresence mode="popLayout">
                    {queue.slice(1).length === 0 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-12 text-zinc-500"
                      >
                        <Music2 size={32} className="mx-auto mb-3 opacity-40" />
                        <div className="text-sm">No songs queued yet</div>
                        <div className="text-xs text-zinc-600 mt-1">
                          Have guests scan the QR to start
                        </div>
                      </motion.div>
                    )}
                    {queue.slice(1).map((q, idx) => (
                      <motion.div
                        layout
                        key={q.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.25 }}
                        className="flex items-center gap-2.5 p-2.5 rounded-2xl bg-zinc-900/50 border border-white/5"
                        data-testid={`queue-item-mobile-${q.id}`}
                      >
                        <div className="w-6 h-6 rounded-full bg-zinc-800 text-zinc-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">
                            {decodeHtmlEntities(q.title)}
                          </div>
                          <div className="text-zinc-500 text-xs truncate">
                            {q.requested_by_name} · {formatDuration(q.duration)}
                          </div>
                        </div>
                        <button
                          data-testid={`remove-queue-mobile-${q.id}`}
                          onClick={() => removeItem(q.id)}
                          className="text-zinc-500 hover:text-red-400 text-lg w-6"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      <GuestsPanel
        open={showGuests}
        onClose={() => setShowGuests(false)}
        guests={guests}
        onKick={handleKick}
        onBan={handleBan}
      />

      <AddSongModal
        open={showAddSong}
        onClose={() => setShowAddSong(false)}
        code={code}
        hostToken={hostToken}
        hostNickname={hostNickname}
      />
    </div>
  );
}

function KaraokeInterludeOverlay({
  countdownMs,
  currentTitle,
  hasUpcomingSong,
  finishedSong,
  label,
  phrase,
  score,
}) {
  const secondsLeft = Math.max(1, Math.ceil(countdownMs / 1000));
  const hasNext = Boolean(hasUpcomingSong);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="karaoke-interlude absolute inset-0 z-40 flex items-center justify-center px-3 sm:px-4"
      data-testid="karaoke-interlude"
    >
      <div className="karaoke-interlude__scanlines" />
      <div className="karaoke-interlude__lights" />
      <div className="relative z-10 w-full max-w-3xl">
        <div className="karaoke-interlude__scoreboard text-center">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.35em] text-amber-200/80">
            Performance Recap
          </div>
          <div className="mt-3 text-6xl font-black leading-none text-white font-[Outfit] sm:text-8xl">
            {score}
          </div>
          <div className="mt-2 text-base sm:text-lg font-semibold uppercase tracking-[0.12em] sm:tracking-[0.2em] text-amber-300">
            {label}
          </div>
          <div className="mt-4 text-xl font-semibold text-white sm:text-3xl leading-tight">{phrase}</div>
          <div className="mt-5 h-px w-full bg-gradient-to-r from-amber-400/50 via-white/20 to-transparent" />
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-black/25 px-4 sm:px-5 py-3 sm:py-4">
              <div className="text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.3em] text-zinc-400">
                Just Finished
              </div>
              <div className="mt-2 text-lg sm:text-xl font-semibold text-white line-clamp-2">
                {decodeHtmlEntities(finishedSong?.title || "That last chorus")}
              </div>
              <div className="mt-1 text-sm text-zinc-300">
                {finishedSong?.requested_by_name || "OpenKTV Spotlight"}
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-black/25 px-4 sm:px-5 py-3 sm:py-4">
              <div className="text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.3em] text-zinc-400">
                {hasNext ? "Up Next" : "Queue Status"}
              </div>
              <div className="mt-2 text-lg sm:text-xl font-semibold text-white line-clamp-2">
                {hasNext
                  ? decodeHtmlEntities(currentTitle || "Loading next performance")
                  : "No songs left in queue"}
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-zinc-200">
            <div className="karaoke-interlude__countdown">{secondsLeft}</div>
            <div className="text-center sm:text-left">
              <div className="text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.28em] text-zinc-400">
                {hasNext ? "Next song starts in" : "Score screen closes in"}
              </div>
              <div className="text-base sm:text-lg font-semibold text-white">
                {hasNext
                  ? `about ${secondsLeft} seconds`
                  : `returning in about ${secondsLeft} seconds`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function FloatingQR({ joinUrl, code }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.96 }}
      transition={{ duration: 0.76, ease: "easeInOut" }}
      className="absolute bottom-4 right-4 z-20 p-1.5 flex flex-col items-stretch gap-1 rounded-xl border border-[#18181ba6] bg-[#18181ba6] shadow-[0_0_18px_rgba(255,97,0,0.48)]"
      data-testid="floating-qr"
    >
      <div className="text-[11px] uppercase tracking-[0.08em] text-white font-bold leading-tight text-center whitespace-nowrap px-1">
        Join the fun
      </div>
      <div className="bg-white rounded-sm p-1 flex items-center justify-center">
        <QRCodeSVG
          value={joinUrl}
          size={74}
          bgColor="#ffffff"
          fgColor="#000000"
          level="M"
          className="block"
        />
      </div>
      <div
        className="text-white font-bold text-[11px] font-[Outfit] leading-tight text-center tracking-[0.08em] px-1"
        title={code}
      >
        {code}
      </div>
    </motion.div>
  );
}

function AddSongModal({ open, onClose, code, hostToken, hostNickname }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState({
    cacheHit: false,
    youtubeAvailable: true,
    totalLocalResults: 0,
  });
  const [searching, setSearching] = useState(false);
  const [youtubeSearching, setYoutubeSearching] = useState(false);
  const [adding, setAdding] = useState({});
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      setSearchMeta({ cacheHit: false, youtubeAvailable: true, totalLocalResults: 0 });
      return;
    }
  }, [open]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q.trim()) {
      setResults([]);
      setSearchMeta({ cacheHit: false, youtubeAvailable: true, totalLocalResults: 0 });
      return;
    }
    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setSearching(true);
      try {
        const data = await searchKaraoke(q.trim(), ac.signal);
        setResults(data.results || []);
        setSearchMeta({
          cacheHit: !!data.cacheHit,
          youtubeAvailable: data.youtubeAvailable !== false,
          totalLocalResults: Number(data.totalLocalResults || 0),
        });
      } catch (e) {
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          toast.error("Search failed");
        }
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const runYouTubeSearch = async () => {
    const query = q.trim();
    if (!query) return;
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setYoutubeSearching(true);
    try {
      const data = await searchYouTube(query, ac.signal);
      setResults(data.results || []);
      setSearchMeta({ cacheHit: false, youtubeAvailable: data.youtubeAvailable !== false, totalLocalResults: 0 });
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
        toast.error("YouTube search failed");
      }
    } finally {
      setYoutubeSearching(false);
    }
  };

  const handleAdd = async (r) => {
    setAdding((s) => ({ ...s, [r.video_id]: true }));
    try {
      await addToQueueAsHost(
        code,
        {
          video_id: r.video_id,
          title: r.title,
          channel: r.channel,
          thumbnail: r.thumbnail,
          duration: r.duration,
          source: r.source,
        },
        hostToken,
        hostNickname
      );
      toast.success(`Added: ${decodeHtmlEntities(r.title || "").slice(0, 40)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setAdding((s) => ({ ...s, [r.video_id]: false }));
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-40"
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
            data-testid="add-song-modal"
          >
            <div className="flex h-[86vh] sm:h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-purple-500/30 bg-transparent glass shadow-[0_0_60px_rgba(139,92,246,0.35)]">
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-white/5 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300">
                    Host · Direct Add
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white font-[Outfit]">
                    Add a Song
                  </h3>
                </div>
                <button
                  data-testid="close-add-song"
                  onClick={onClose}
                  className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-white"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="px-4 sm:px-6 pt-4 pb-3">
                <div className="relative">
                  <Search
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
                  />
                  <input
                    data-testid="host-search-input"
                    autoFocus
                    type="text"
                    className="input-base pl-11 pr-10"
                    placeholder="Search karaoke tracks..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                  {q && (
                    <button
                      onClick={() => setQ("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-zinc-800 flex items-center justify-center text-zinc-500"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-6 space-y-2">
                {searching && (
                  <div className="flex items-center justify-center py-10 text-zinc-500">
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Searching...
                  </div>
                )}
                {!searching && !q && (
                  <div className="text-center py-16 text-zinc-500">
                    <Music2 size={32} className="mx-auto mb-3 opacity-40" />
                    Type to search — we append &ldquo;karaoke&rdquo; automatically.
                  </div>
                )}
                {!searching && q && results.length === 0 && (
                  <div className="text-center py-14 text-zinc-500">
                    No results.
                  </div>
                )}
                {!searching && searchMeta.cacheHit && searchMeta.totalLocalResults > 0 && (
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 mb-1">
                    <div className="text-emerald-200 font-semibold text-sm">
                      Found {searchMeta.totalLocalResults} songs in OpenKTV Catalog
                    </div>
                  </div>
                )}
                {results.map((r) => (
                  <div
                    key={r.video_id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3 p-2.5 rounded-2xl bg-zinc-900/60 border border-white/5 hover:border-purple-500/30 transition-colors"
                    data-testid={`host-search-result-${r.video_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium leading-tight line-clamp-2">
                        {decodeHtmlEntities(r.title)}
                      </div>
                      <div className="text-zinc-500 text-xs mt-0.5 truncate">
                        {decodeHtmlEntities(r.channel)} · {formatDuration(r.duration)} ·{" "}
                        {formatViews(r.view_count)} views
                      </div>
                    </div>
                    <button
                      data-testid={`host-add-song-${r.video_id}`}
                      disabled={adding[r.video_id]}
                      onClick={() => handleAdd(r)}
                      className="w-full sm:w-auto px-3 py-2 sm:py-1.5 rounded-lg bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center transition-all flex-shrink-0"
                    >
                      {adding[r.video_id] ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        "Add to Queue"
                      )}
                    </button>
                  </div>
                ))}
                {!searching && searchMeta.cacheHit && results.length > 0 && (
                  <div className="mt-3 rounded-2xl border border-white/10 bg-zinc-900/50 px-4 py-4">
                    <div className="text-zinc-300 text-sm mb-3">Can&apos;t find your song?</div>
                    <button
                      type="button"
                      data-testid="host-search-youtube-btn"
                      onClick={runYouTubeSearch}
                      disabled={youtubeSearching}
                      className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-60 text-white text-sm font-medium"
                    >
                      {youtubeSearching ? "Searching YouTube..." : "Search YouTube"}
                    </button>
                  </div>
                )}
              </div>
              <div className="px-4 sm:px-6 py-3 border-t border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
                Shortcut: A to open · Esc to close
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function GuestsPanel({ open, onClose, guests, onKick, onBan }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] z-50 glass border-l border-white/10 flex flex-col"
            data-testid="guests-panel"
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-white/5 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Connected
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white font-[Outfit]">
                  Guests ({guests.length})
                </h3>
              </div>
              <button
                data-testid="close-guests-panel"
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {guests.length === 0 && (
                <div className="text-center py-16 text-zinc-500">
                  <Users size={28} className="mx-auto mb-3 opacity-40" />
                  No guests yet
                </div>
              )}
              {guests.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center gap-2.5 sm:gap-3 p-3 rounded-2xl bg-zinc-900/70 border border-white/5"
                  data-testid={`guest-row-${g.id}`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                    style={{ background: g.avatar_color }}
                  >
                    {(g.nickname || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold truncate">
                      {g.nickname}
                    </div>
                    <div className="text-zinc-500 text-xs">Guest</div>
                  </div>
                  <button
                    data-testid={`kick-${g.id}`}
                    onClick={() => onKick(g.id, g.nickname)}
                    className="px-2.5 py-2 rounded-lg bg-zinc-800 hover:bg-orange-500/20 text-orange-300 hover:text-orange-200 text-xs font-semibold flex items-center gap-1 border border-transparent hover:border-orange-500/30 transition-all"
                  >
                    <UserX size={13} /> Kick
                  </button>
                  <button
                    data-testid={`ban-${g.id}`}
                    onClick={() => onBan(g.id, g.nickname)}
                    className="px-2.5 py-2 rounded-lg bg-zinc-800 hover:bg-red-500/20 text-red-300 hover:text-red-200 text-xs font-semibold flex items-center gap-1 border border-transparent hover:border-red-500/30 transition-all"
                  >
                    <Ban size={13} /> Ban
                  </button>
                </div>
              ))}
            </div>
            <div className="px-6 py-3 border-t border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
              Shortcut: G to toggle
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function IdleScreen({ joinUrl, code, guests }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center flex-col gap-6 sm:gap-8 p-5 sm:p-8 lg:p-12 bg-gradient-to-br from-purple-950/30 via-zinc-950 to-fuchsia-950/20">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="bg-white p-4 sm:p-6 rounded-3xl shadow-[0_0_60px_rgba(139,92,246,0.4)]">
          <QRCodeSVG
            value={joinUrl}
            size={220}
            bgColor="#ffffff"
            fgColor="#0a0a0a"
            level="M"
          />
        </div>
      </motion.div>

      <div className="text-center">
        <div className="text-zinc-400 text-sm uppercase tracking-[0.3em] mb-3">
          Scan to Join — Or Visit
        </div>
        <div className="text-zinc-300 text-base sm:text-xl font-mono break-all">
          {window.location.host}/join/{code}
        </div>
        <div className="mt-6 sm:mt-8 text-white text-2xl sm:text-4xl lg:text-5xl font-bold font-[Outfit] text-glow-purple text-center">
          Please select a song
        </div>
        <div className="mt-3 text-zinc-500">
          {guests} {guests === 1 ? "guest" : "guests"} connected
        </div>
      </div>
    </div>
  );
}
